import { Stack } from '@mui/material'
import { C1Button } from 'c1ui'
import { PluralFacetFilter } from 'components/core/search-filter/facet'
import { QueryFilter } from 'components/core/search-filter/query'
import { MetaFields } from 'lib/search'
import { AccessReviewSearchServiceSearchEntitlementsRequest } from 'pbts/c1/api/accessreview/v1/accessreview_search'
import { App } from 'pbts/c1/api/app/v1/app'
import {
    AppEntitlementSearchServiceSearchRequest,
    AppEntitlementSearchServiceSearchTermsRequest,
    AppEntitlementSearchServiceSearchTermsRequest_TermType as TermType,
} from 'pbts/c1/api/app/v1/app_entitlement'
import { SearchAppResourceRollupRequest } from 'pbts/c1/api/app/v1/app_resource'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { appEntitlementSearchTermsAPI } from 'redux_toolkit/entitlements'

type FilterKeys = keyof Omit<AccessReviewSearchServiceSearchEntitlementsRequest, MetaFields>
type FilterUpdate = Partial<Pick<AccessReviewSearchServiceSearchEntitlementsRequest, FilterKeys>>
type FilterOnChange = (updated: FilterUpdate) => void

type AppResourceCampaignFiltersProps = {
    filter: SearchAppResourceRollupRequest
    onQueryChange: (q: string, isloading: boolean) => void
    canClear: boolean
    clear?: () => void
    onAppChange: (selected: App[], loading: boolean) => void
    onChange: FilterOnChange
}

export const AppResourceCampaignFilters: React.FC<AppResourceCampaignFiltersProps> = ({
    filter,
    onQueryChange,
    canClear,
    clear,
    onChange,
}) => {
    const correctedType = useMemo(() => AppEntitlementSearchServiceSearchRequest.create(filter), [filter])
    const intl = useIntl()

    return <Stack direction="row" spacing={2}>
        <QueryFilter
            autoFocus
            query={filter?.query || ''}
            setQuery={(q): void => onQueryChange(q as unknown as string, true)}
            onChange={onQueryChange}
        />
        <PluralFacetFilter<AppEntitlementSearchServiceSearchTermsRequest, 'appIds'>
            onChange={onChange}
            filter={correctedType}
            term={TermType.APPS}
            filterKey="appIds"
            label={intl.formatMessage({ defaultMessage: 'Application', id: 'application' })}
            hook={appEntitlementSearchTermsAPI.useLazyGetSearchTerms}
        />
        <PluralFacetFilter<AppEntitlementSearchServiceSearchTermsRequest, 'resourceTypeIds'>
            onChange={onChange}
            filter={correctedType}
            term={TermType.RESOURCE_TYPE}
            filterKey="resourceTypeIds"
            label={intl.formatMessage({ defaultMessage: 'Resource type', id: 'resource_type' })}
            hook={appEntitlementSearchTermsAPI.useLazyGetSearchTerms}
            showCount={false}
        />
        <PluralFacetFilter<AppEntitlementSearchServiceSearchTermsRequest, 'riskLevelIds'>
            onChange={onChange}
            filter={correctedType}
            term={TermType.RISK_LEVEL}
            filterKey="riskLevelIds"
            label={intl.formatMessage({ defaultMessage: 'Risk level', id: 'risk_level' })}
            hook={appEntitlementSearchTermsAPI.useLazyGetSearchTerms}
        />
        <PluralFacetFilter<AppEntitlementSearchServiceSearchTermsRequest, 'complianceFrameworkIds'>
            onChange={onChange}
            filter={correctedType}
            term={TermType.COMPLIANCE_FRAMEWORK}
            filterKey="complianceFrameworkIds"
            label={intl.formatMessage({ defaultMessage: 'Compliance framework', id: 'Compliance framework' })}
            hook={appEntitlementSearchTermsAPI.useLazyGetSearchTerms}
        />
        {canClear && (
            <C1Button onClick={clear} variant="text" color="info">
                <FormattedMessage id="components_task_search_index_clear" defaultMessage="Clear" />
            </C1Button>
        )}
    </Stack>
}
